<Boundary>
  <div class="wrapper" style={internalStyle} {...parentAttributes}>
    {#if showEditorControls}
      <div class="controls" class:clone={subtreeIsCloned}>
        <CardsEditorComponentControls active={thisSelected} {buttons} />
      </div>
    {/if}
    <slot></slot>
  </div>
</Boundary>
{#if thisSelected}
<Popup class="cards-editor-popup" backdropEl=".cards-editor-popup-backdrop" opened={editPopupOpened} onPopupClosed={() => editPopupOpened = false}>
  <CardsEditorComponentEditPopup
    title="Edit {editorOptions.componentTypeName}"
    barColor={editorOptions.primaryColor}
    textColor={editorOptions.reverseTextColor}
    schema={editorSchema}
    data={editorOptions.component}
    onUpdate={mergeChangesAndUpdate}
    onFileUpload={emm.beginFileUpload.bind(emm)}
    closePopup={() => editPopupOpened = false}
  />
</Popup>
{/if}

<style>*, *::before, *::after {
  box-sizing: border-box;
}

.wrapper {
  /* min-height: 40px; */
}

.controls {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 21px;
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: center;
}

.controls:not(.clone) {
  background-color: var(--cards-editor-component-primary-color);
  color: var(--cards-editor-component-reverse-text-color);
}

.controls.clone {
  border-right: 2px dotted var(--cards-editor-component-primary-color);
  border-bottom: 2px dotted var(--cards-editor-component-primary-color);
  color: var(--cards-editor-component-primary-color);
}</style>

<script lang="ts">import { Boundary } from '@crownframework/svelte-error-boundary/src/index';
import { CardsEditorSubtreeContext, CardsPageContext, CardsPageContextKey } from './context';
import { getContext, setContext, onDestroy } from "svelte";
import { CardsEditorComponentOptions } from './CardsEditorInterfaces';
import CardsEditorComponentControls from './CardsEditorComponentControls.svelte';
import { CardsEditorComponent, CardsEditorComponentChild, CardsEditorFileUploadCallbacks } from './CardsEditorComponentInterfaces';
import _ from 'lodash';
import { Item } from 'svelte-dnd-action';
import { v4 as uuid4 } from "uuid";
import { CardsEditorSubtreeContextKey } from "./context";
import { derived, Readable, writable } from "svelte/store";
import { f7, Popup } from 'framework7-svelte';
import CardsEditorComponentEditPopup from './CardsEditorComponentEditPopup.svelte';
export let style = "";
export let parentAttributes = {};
export let editorOptions;
const pageContext = getContext(CardsPageContextKey);
const emm = pageContext.editorModeManager;
const emmEnabledStore = emm.getEnabledStore();
const emmPreviewStore = emm.getPreviewStore();
const emmSelectedStore = emm.getSelectedComponentStore();
const editorOptionsStore = writable(editorOptions);
const thisPreviewStore = writable(false);
const parentSubtreeContextStore = getContext(CardsEditorSubtreeContextKey) || writable(null);
const thisSubtreeContextStore = derived([parentSubtreeContextStore, editorOptionsStore, thisPreviewStore], ([parentSubtreeContext, thisEditorOptions, thisPreview], set) => {
    var _a, _b;
    const thisCloned = (_.isNil(editorOptions.cloned)) ? !!((_b = (_a = editorOptions.component) === null || _a === void 0 ? void 0 : _a.__expandRepeats) === null || _b === void 0 ? void 0 : _b.cloned) : editorOptions.cloned;
    set({
        componentId: thisEditorOptions.componentId,
        component: thisEditorOptions.component,
        subtreeCloned: (parentSubtreeContext === null || parentSubtreeContext === void 0 ? void 0 : parentSubtreeContext.subtreeCloned) || thisCloned,
        subtreePreview: (parentSubtreeContext === null || parentSubtreeContext === void 0 ? void 0 : parentSubtreeContext.subtreePreview) || thisPreview,
        /* --- */
        moveUpChild,
        moveDownChild,
        cloneChild,
        removeChild
    });
});
setContext(CardsEditorSubtreeContextKey, thisSubtreeContextStore);
let internalStyle = style;
let emmEnabled;
let emmPreview;
let emmSelected;
let parentSelected = false;
let thisSelected = false;
let showEditorControls = false;
let buttons;
let editPopupOpened = false;
let editorSchema = generateSchema();
$: emmEnabled = $emmEnabledStore;
$: emmPreview = $emmPreviewStore;
$: emmSelected = $emmSelectedStore;
$: parentSelected = (emmSelected === null || emmSelected === void 0 ? void 0 : emmSelected.sequenceId) === editorOptions.parentSequenceId;
$: thisSelected = (emmSelected === null || emmSelected === void 0 ? void 0 : emmSelected.sequenceId) === editorOptions.sequenceId;
$: outlineMode = emmEnabled && !emmPreview;
$: showEditorControls = outlineMode;
$: hasChldren = getOrCreateChildrenArray(editorOptions).length > 0;
$: $editorOptionsStore = editorOptions;
//$: $thisIsClonedStore = (_.isNil(editorOptions.cloned))? !!editorOptions.component?.__expandRepeats?.cloned : editorOptions.cloned;
$: subtreeIsCloned = $thisSubtreeContextStore.subtreeCloned;
$: {
    let css = style;
    if (outlineMode) {
        css += `--cards-editor-component-primary-color: ${editorOptions.primaryColor};`;
        css += `--cards-editor-component-reverse-text-color: ${editorOptions.reverseTextColor};`;
        if (subtreeIsCloned) {
            css += `border: 2px dotted var(--cards-editor-component-primary-color);`;
        }
        else if (parentSelected) {
            css += 'border: 2px dashed lightpink;';
        }
        else if (thisSelected) {
            css += 'border: 2px solid red;';
        }
        else {
            css += `border: 2px solid var(--cards-editor-component-primary-color);`;
        }
        css += 'padding: 5px;';
        css += 'background: initial;';
        css += 'margin: initial;';
    }
    else {
        // if not in outline mode, apply the styles coming from the parent
        // css += style;
    }
    if (showEditorControls) {
        css += 'position: relative;';
        if (hasChldren) {
            css += 'padding-top: 28px;';
        }
    }
    internalStyle = css;
}
$: if (subtreeIsCloned) {
    buttons = [
        { id: 'NAME', label: editorOptions.componentTypeName },
    ];
}
else {
    buttons = [
        { id: 'NAME', label: editorOptions.componentTypeName, onClick: onNameClick },
    ];
    if (editorSchema) {
        buttons.push({ id: 'EDIT', icon: 'square_pencil_fill', tooltip: 'Edit this component.', showOnlyWhenActive: true, onClick: onEdit });
    }
    if ($parentSubtreeContextStore === null || $parentSubtreeContextStore === void 0 ? void 0 : $parentSubtreeContextStore.componentId) {
        buttons.push({ id: 'SELECT_PARENT', icon: 'arrow_up_left_square_fill', tooltip: 'Select parent component.', showOnlyWhenActive: true, onClick: onSelectParent });
    }
    if (editorOptions.allowMovement) {
        buttons.push({ id: 'MOVE_PREV', icon: 'arrowtriangle_up_fill', tooltip: 'Move component up in order.', showOnlyWhenActive: true, onClick: onMoveUp });
        buttons.push({ id: 'MOVE_NEXT', icon: 'arrowtriangle_down_fill', tooltip: 'Move component down in order.', showOnlyWhenActive: true, onClick: onMoveDown });
    }
    if (editorOptions.allowRepeats) {
        buttons.push({ id: 'ADD_REPEAT', icon: 'rectangle_stack_fill_badge_plus', tooltip: 'Increase the number of times this component is repeated in order. Repeated components share the same configuration as the original component.', showOnlyWhenActive: true, onClick: onAddRepeat });
        buttons.push({ id: 'REMOVE_REPEAT', icon: 'rectangle_stack_fill_badge_minus', tooltip: 'Decrease the number of times this component is repeated in order. Repeated components share the same configuration as the original component.', showOnlyWhenActive: true, onClick: onRemoveRepeat });
    }
    if (editorOptions.allowDuplication) {
        buttons.push({ id: 'CLONE', icon: 'doc_on_doc_fill', tooltip: 'Create an independent copy of this component. Copied components do not share any configuration with the original component.', showOnlyWhenActive: true, onClick: onClone });
    }
    if (editorOptions.allowChildren) {
        buttons.push({ id: 'ADD_CHILD', icon: 'plus_square_fill_on_square_fill', tooltip: 'Add a new child to this component.', showOnlyWhenActive: true, onClick: onAddChild });
    }
    if (editorOptions.allowDeletion) {
        buttons.push({ id: 'DELETE', icon: 'trash_fill', tooltip: 'Delete this component.', showOnlyWhenActive: true, onClick: onDelete });
    }
}
function getOrCreateChildrenArray(editorOptions) {
    const c = editorOptions.component;
    const k = editorOptions.childrenKey;
    if (!_.isArray(c[k])) {
        c[k] = [];
        doUpdate();
    }
    return c[k];
}
function onEdit() {
    editPopupOpened = true;
}
function onNameClick() {
    emm.setSelectedComponentId(editorOptions.componentId);
}
function onSelectParent() {
    if ($parentSubtreeContextStore) {
        emm.setSelectedComponentId($parentSubtreeContextStore.componentId);
    }
}
function onMoveUp() {
    $parentSubtreeContextStore === null || $parentSubtreeContextStore === void 0 ? void 0 : $parentSubtreeContextStore.moveUpChild(editorOptions.componentId);
}
function onMoveDown() {
    $parentSubtreeContextStore === null || $parentSubtreeContextStore === void 0 ? void 0 : $parentSubtreeContextStore.moveDownChild(editorOptions.componentId);
}
function onAddRepeat() {
    //console.log("ON ADD REPEAT", editorOptions.component);
    if (_.isFunction(editorOptions.addRepeat)) {
        editorOptions.addRepeat();
    }
    else {
        editorOptions.component.repeat = Math.max((editorOptions.component.repeat || 1) + 1, 1);
    }
    doUpdate();
}
function onRemoveRepeat() {
    //console.log("ON REMOVE REPEAT", editorOptions.component);
    if (_.isFunction(editorOptions.removeRepeat)) {
        editorOptions.removeRepeat();
    }
    else {
        editorOptions.component.repeat = (editorOptions.component.repeat || 1) - 1;
        if (editorOptions.component.repeat <= 1) {
            delete editorOptions.component.repeat;
        }
    }
    doUpdate();
}
function onClone() {
    $parentSubtreeContextStore === null || $parentSubtreeContextStore === void 0 ? void 0 : $parentSubtreeContextStore.cloneChild(editorOptions.componentId);
}
function onAddChild() {
    addChild();
}
function onDelete() {
    $parentSubtreeContextStore === null || $parentSubtreeContextStore === void 0 ? void 0 : $parentSubtreeContextStore.removeChild(editorOptions.componentId);
}
/* */
function doUpdate() {
    editorOptions.updater(editorOptions.component);
    emm.notifyPageDataUpdate();
}
function createChild() {
    return (_.isFunction(editorOptions.createChild)) ?
        editorOptions.createChild() :
        { id: uuid4() };
}
function addChild() {
    const newChild = createChild();
    console.log('ADD CHILD', newChild);
    getOrCreateChildrenArray(editorOptions).push(newChild);
    doUpdate();
}
function moveUpChild(id) {
    const arr = getOrCreateChildrenArray(editorOptions);
    let idx = arr.findIndex((i) => i.id === id);
    if (idx > 0) {
        const temp = arr[idx - 1];
        arr[idx - 1] = arr[idx];
        arr[idx] = temp;
        doUpdate();
    }
}
function moveDownChild(id) {
    const arr = getOrCreateChildrenArray(editorOptions);
    let idx = arr.findIndex((i) => i.id === id);
    if (idx >= 0 && idx < arr.length - 1) {
        const temp = arr[idx + 1];
        arr[idx + 1] = arr[idx];
        arr[idx] = temp;
        doUpdate();
    }
}
function cloneChild(id) {
    const arr = getOrCreateChildrenArray(editorOptions);
    let idx = arr.findIndex((i) => i.id === id);
    if (idx >= 0) {
        arr.splice(idx + 1, 0, _.cloneDeepWith(arr[idx], (v, k) => {
            if (k === 'id' && _.isString(v)) {
                return uuid4();
            }
        }));
        doUpdate();
    }
}
function removeChild(id) {
    const arr = getOrCreateChildrenArray(editorOptions);
    let idx = arr.findIndex((i) => i.id === id);
    if (idx >= 0) {
        arr.splice(idx, 1);
        doUpdate();
    }
}
function getChildren() {
    return getOrCreateChildrenArray(editorOptions).map((i) => {
        return { id: i.id };
    });
}
function getConfigValue(key) {
    var _a;
    return (_a = editorOptions === null || editorOptions === void 0 ? void 0 : editorOptions.component) === null || _a === void 0 ? void 0 : _a[key];
}
function setConfigValue(key, value) {
    if (editorOptions === null || editorOptions === void 0 ? void 0 : editorOptions.component) {
        editorOptions.component[key] = value;
        doUpdate();
    }
}
function generateSchema() {
    return (_.isFunction(editorOptions.configSpecGenerator)) ?
        editorOptions.configSpecGenerator(editorOptions.component) :
        editorOptions.configSpec;
}
function mergeChangesAndUpdate(data) {
    //console.log("mergeChangesAndUpdate before merge", editorOptions.component, data);
    // FIXME: This should do a sweep of specified properties in the json schema
    // and remove properties that are defined in the schema but are not in the data object
    _.merge(editorOptions.component, data);
    //console.log("mergeChangesAndUpdate after merge", editorOptions.component);
    doUpdate();
    // For performance reasons, this is only regenerated here.
    editorSchema = generateSchema();
}
$: emm.registerComponent({
    id: editorOptions.componentId,
    sequenceId: editorOptions.sequenceId,
    configSpec: { options: [] },
    addChild,
    removeChild,
    getChildren,
    getConfigValue,
    setConfigValue
});
onDestroy(() => {
    emm.unregisterComponent(editorOptions.componentId);
});
</script>
